import { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Archive, ArchiveRestore, Loader2 } from "lucide-react";

/**
 * Issue #166 FAC-11 — archive a venue record or one of its surfaces, and
 * restore it, from wherever the record is being managed.
 *
 * ONE COMPONENT FOR BOTH TABLES, like `ConditionUpdate`: a ground and a net
 * archive the same way, differ only in where the POST is addressed, and two
 * implementations of one gesture is how the two would drift. `basePath` is
 * the record's own route (`/grounds/:id` or `/grounds/:id/facilities/:fid`);
 * the verbs `/archive` and `/unarchive` hang off it.
 *
 * NO CONFIRMATION DIALOG, deliberately. Archiving is REVERSIBLE — that is
 * its whole difference from deletion — and the interruption cost of a
 * confirm falls on a gesture whose mistake costs one more tap to undo. It
 * also keeps this out of `dialog-config.js`'s curation surface: no dialog,
 * nothing to curate. What replaces the confirm is the caption, which says
 * what the state change does BEFORE the tap.
 *
 * WHO SEES IT: the caller gates on the same predicate as every other edit
 * affordance (`canOfferGroundEdit` — a facility has no gate of its own,
 * #165), and the server re-decides with `checkGroundCorrectionGate` anyway.
 * A refusal is shown, not swallowed, `GroundEditPanel`'s way.
 *
 * `onDone` receives the SERVER's row — `archived_at` is server-stamped
 * (#164's rule applied to a lifecycle date), so echoing a locally-guessed
 * timestamp would date the state by the device that tapped.
 */
export default function ArchiveToggle({ basePath, archived = false, noun = "ground", onDone, testId }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function toggle() {
    if (busy || !basePath) return;
    setBusy(true);
    setError("");
    try {
      const res = await api.post(`${basePath}/${archived ? "unarchive" : "archive"}`);
      if (onDone) onDone(res.data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2" data-testid={testId}>
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        data-testid={testId ? `${testId}-button` : undefined}
        /* 44px minimum, the page-wide tap height (#134). */
        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 min-h-11 text-[11px] font-semibold text-slate-600 transition hover:border-slate-400 disabled:opacity-60"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : archived ? <ArchiveRestore size={12} /> : <Archive size={12} />}
        {archived ? `Restore this ${noun}` : `Archive this ${noun}`}
      </button>
      <p className="mt-1 text-[10px] text-slate-500" data-testid={testId ? `${testId}-help` : undefined}>
        {archived
          ? `Restoring puts this ${noun} back in the pickers so new matches and sessions can use it.`
          : `Archiving hides this ${noun} from the pickers — everything already logged here keeps it. You can restore it any time.`}
      </p>
      {error && (
        <p className="mt-1 text-xs text-red-600" role="alert" data-testid={testId ? `${testId}-error` : undefined}>
          {error}
        </p>
      )}
    </div>
  );
}
